/**
 * Milk, one session at a time (§6.5, P2-TR3).
 *
 * A stepper per cow rather than a text field. Yields sit between 3 and 12
 * litres and change by half a litre; typing "6.5" on a phone keyboard while
 * holding a milk pail is slower and wronger than tapping + twice.
 *
 * One save for the whole session. A request per cow on a rural connection is
 * eight chances to fail with no way to tell which of them landed — and a
 * repeat submission corrects rather than adds, so tapping save twice is safe.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAnimals } from '../animals/api.js';
import { useSaveMilkLog } from './api.js';

/** Half a litre: the smallest amount anybody eyeballs in a pail. */
const STEP = 0.5;
const MAX_LITRES = 60;

/** Kept as a string end to end; the arithmetic is on tenths, not on floats. */
export function stepLitres(current: string, direction: 1 | -1): string {
  const tenths = Math.round(Number(current || '0') * 10) + direction * STEP * 10;
  const bounded = Math.min(MAX_LITRES * 10, Math.max(0, tenths));
  return (bounded / 10).toFixed(2);
}

export function MilkEntry(): JSX.Element {
  const { t } = useTranslation();
  const save = useSaveMilkLog();

  const [session, setSession] = useState<'morning' | 'evening'>(
    // Before noon in Dhaka is the morning milking; after it, the evening one.
    new Date().getUTCHours() < 6 ? 'morning' : 'evening',
  );
  const [litres, setLitres] = useState<Record<number, string>>({});
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedTotal, setSavedTotal] = useState<string | null>(null);

  // Only cows: the server refuses anything else, and offering a goat here
  // would be offering a mistake.
  const { data } = useAnimals({ species: 'cattle', status: 'active', limit: 100 });
  const cows = (data?.items ?? []).filter((animal) => animal.sex === 'female');

  const entered = cows.filter((cow) => litres[cow.id] !== undefined);
  const total = entered.reduce((sum, cow) => sum + Number(litres[cow.id] ?? '0'), 0);

  async function handleSubmit(): Promise<void> {
    setErrorKey(null);

    try {
      const result = await save.mutateAsync({
        date: todayInDhaka(),
        session,
        lines: entered.map((cow) => ({ animalId: cow.id, litres: litres[cow.id] ?? '0' })),
      });

      setSavedTotal(result.totalLitres);
      setLitres({});
    } catch (error) {
      // The readings stay on screen: retyping eight cows is how a session
      // goes unrecorded (NFR-2).
      setErrorKey(errorMessageKey(error));
    }
  }

  return (
    <form
      className="page-container"
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      noValidate
    >
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('milk.title')}</h1>

      <div className="mb-4 flex gap-2" role="group" aria-label={t('milk.session')}>
        {(['morning', 'evening'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setSession(option)}
            aria-pressed={session === option}
            className={`min-h-touch flex-1 rounded-lg px-3 py-2 text-sm ${
              session === option ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(`milk.${option}`)}
          </button>
        ))}
      </div>

      {cows.length === 0 && <p className="py-8 text-center text-gray-500">{t('milk.noCows')}</p>}

      <ul className="space-y-2">
        {cows.map((cow) => {
          const value = litres[cow.id] ?? '0.00';

          return (
            <li key={cow.id} className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm">
              <span className="flex-1 font-medium text-gray-800">{cow.tag}</span>

              <button
                type="button"
                aria-label={t('milk.less', { tag: cow.tag })}
                onClick={() => setLitres((current) => ({ ...current, [cow.id]: stepLitres(value, -1) }))}
                className="min-h-touch min-w-touch rounded-lg bg-gray-100 text-xl text-gray-700"
              >
                −
              </button>

              <output
                aria-label={t('milk.litresFor', { tag: cow.tag })}
                className="w-16 text-center tabular-nums text-lg font-semibold text-gray-900"
              >
                {value}
              </output>

              <button
                type="button"
                aria-label={t('milk.more', { tag: cow.tag })}
                onClick={() => setLitres((current) => ({ ...current, [cow.id]: stepLitres(value, 1) }))}
                className="min-h-touch min-w-touch rounded-lg bg-green-100 text-xl text-green-800"
              >
                +
              </button>
            </li>
          );
        })}
      </ul>

      {errorKey !== null && (
        <p role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      {savedTotal !== null && errorKey === null && (
        <p role="status" className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('milk.saved', { litres: savedTotal })}
        </p>
      )}

      <button
        type="submit"
        disabled={entered.length === 0 || save.isPending}
        className="mt-4 min-h-[52px] w-full rounded-xl bg-green-700 text-lg font-medium text-white disabled:opacity-50"
      >
        {t(save.isPending ? 'milk.saving' : 'milk.submit', { litres: total.toFixed(2) })}
      </button>
    </form>
  );
}
